import React, { useEffect, useState } from 'react';
import { Drawer, Empty, Spin, Timeline, message } from 'antd';
import {
  PlusCircleOutlined,
  RobotOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  SwapOutlined,
  ApartmentOutlined,
  CheckCircleOutlined,
  UndoOutlined,
  TeamOutlined,
  PartitionOutlined,
} from '@ant-design/icons';
import { liveChatApi } from '../../utils/api';
import { operatorName } from './liveChatMeta';

const opName = (op, fallback) => (op ? operatorName(op) : fallback || '—');

const actorLabel = (e) => {
  if (e.actor_type === 'contact') return 'مشتری';
  if (e.actor_type === 'system') return 'سیستم';
  return e.actor?.name || 'کاربر';
};

/** متن و ظاهر هر نوع رویداد */
const describe = (e) => {
  const meta = e.meta || {};
  const from = opName(e.from_operator, meta.from_operator_name);
  const to = opName(e.to_operator, meta.to_operator_name);
  const fromDep = e.from_department?.name || meta.from_department_name || 'بدون بخش';
  const toDep = e.to_department?.name || meta.to_department_name || 'بدون بخش';
  switch (e.type) {
    case 'created':
      return { icon: <PlusCircleOutlined />, color: 'green', text: `گفتگو شروع شد${e.to_department || meta.to_department_name ? ` (بخش ${toDep})` : ''}` };
    case 'auto_assigned':
      return { icon: <RobotOutlined />, color: 'gray', text: `ارجاع خودکار به ${to}` };
    case 'claimed':
      return { icon: <UserAddOutlined />, color: 'blue', text: `${to} گفتگو را برداشت${meta.via === 'reply' ? ' (با پاسخ)' : ''}` };
    case 'assigned':
      return { icon: <TeamOutlined />, color: 'blue', text: e.from_operator_id || meta.from_operator_name ? `ارجاع از ${from} به ${to}` : `ارجاع به ${to}` };
    case 'unassigned':
      return { icon: <UserDeleteOutlined />, color: 'orange', text: `ارجاع ${from} لغو شد` };
    case 'transferred':
      return { icon: <SwapOutlined />, color: 'gold', text: `ارجاع به همکار: از ${from} به ${to}` };
    case 'department_changed':
      return {
        icon: <ApartmentOutlined />,
        color: 'purple',
        text: `تغییر بخش: ${fromDep} ← ${toDep}${meta.operator_unassigned ? ` (ارجاع ${from} برداشته شد)` : ''}${
          meta.from_sub_department_name ? ` (زیربخش ${meta.from_sub_department_name} برداشته شد)` : ''
        }`,
      };
    case 'sub_department_changed':
      return {
        icon: <PartitionOutlined />,
        color: 'cyan',
        text: meta.to_sub_department_name
          ? `زیربخش: ${meta.from_sub_department_name ? `${meta.from_sub_department_name} ← ` : ''}${meta.to_sub_department_name}`
          : `زیربخش ${meta.from_sub_department_name || ''} برداشته شد`,
      };
    case 'closed':
      return { icon: <CheckCircleOutlined />, color: 'gray', text: 'گفتگو بسته شد' };
    case 'reopened':
      return { icon: <UndoOutlined />, color: 'green', text: `گفتگو دوباره باز شد${meta.via === 'reply' ? ' (با پاسخ اپراتور)' : ''}` };
    default:
      return { icon: null, color: 'gray', text: e.type };
  }
};

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('fa-IR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
  } catch {
    return value;
  }
};

/** تاریخچه رویدادهای مهم گفتگو (ارجاع، تغییر بخش، بستن، بازگشایی و ...) */
const ConversationHistoryDrawer = ({ open, conversationId, onClose }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !conversationId) return undefined;
    let cancelled = false;
    setLoading(true);
    liveChatApi
      .getEvents(conversationId)
      .then((res) => {
        if (!cancelled) setEvents(res?.data ?? []);
      })
      .catch((error) => {
        if (!cancelled) message.error(error?.message || 'خطا در دریافت تاریخچه');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, conversationId]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={<span className="custom-font">تاریخچه گفتگو</span>}
      placement="left"
      size={380}
      destroyOnHidden
      className="custom-font"
    >
      <Spin spinning={loading}>
        {!loading && events.length === 0 ? (
          <Empty description={<span className="custom-font">رویدادی ثبت نشده است</span>} />
        ) : (
          <Timeline
            items={events.map((e) => {
              const { icon, color, text } = describe(e);
              return {
                key: e.id,
                color,
                dot: icon,
                children: (
                  <div className="custom-font">
                    <div className="text-sm text-gray-800 dark:text-gray-100">{text}</div>
                    {e.note && (
                      <div className="mt-1 rounded-md bg-amber-50 dark:bg-amber-900/20 px-2 py-1 text-xs text-amber-800 dark:text-amber-200 whitespace-pre-wrap break-words">
                        {e.note}
                      </div>
                    )}
                    <div className="mt-0.5 text-[11px] text-gray-400">
                      {actorLabel(e)} · {formatDateTime(e.created_at)}
                    </div>
                  </div>
                ),
              };
            })}
          />
        )}
      </Spin>
    </Drawer>
  );
};

export default ConversationHistoryDrawer;
